import { useMemo, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { useTranslation } from "react-i18next";
import { useTheme } from "@/contexts/ThemeContext";
import { predictions } from "@/mocks/predictions";
import { districts } from "@/mocks/districts";
import { historicalCases } from "@/mocks/historicalCases";
import { resources } from "@/mocks/resources";
import { alerts } from "@/mocks/alerts";
import { Link } from "react-router-dom";

interface DistrictReport {
  id: string;
  name: string;
  region: string;
  population: number;
  predicted2wk: number;
  predicted1mo: number;
  predicted3mo: number;
  growthRate: number;
  alertLevel: string;
  resourceGap: number;
  activeAlerts: number;
  riskTrend: "Rising" | "Stable" | "Declining";
  weeklyCases: number;
}

const regions = Array.from(new Set(districts.map((d) => d.region)));

function generateDistrictReports(): DistrictReport[] {
  return districts.map((d) => {
    const p2wk = predictions.find((p) => p.districtId === d.id && p.horizon === "2wk");
    const p1mo = predictions.find((p) => p.districtId === d.id && p.horizon === "1mo");
    const p3mo = predictions.find((p) => p.districtId === d.id && p.horizon === "3mo");
    const districtResources = resources.filter((r) => r.districtId === d.id);
    const gap = districtResources.reduce((sum, r) => sum + r.gap, 0);
    const activeAlerts = alerts.filter((a) => a.districtId === d.id && a.status === "Active").length;
    const weeklyCases = historicalCases
      .filter((c) => c.districtId === d.id)
      .slice(-1)[0]?.cases || 0;

    const growth = p2wk?.growthRate || 0;
    const trend: DistrictReport["riskTrend"] =
      growth > 20 ? "Rising" : growth > -5 ? "Stable" : "Declining";

    return {
      id: d.id,
      name: d.name,
      region: d.region,
      population: d.population,
      predicted2wk: p2wk?.predictedCases || 0,
      predicted1mo: p1mo?.predictedCases || 0,
      predicted3mo: p3mo?.predictedCases || 0,
      growthRate: growth,
      alertLevel: p2wk?.alertLevel || "Low",
      resourceGap: gap,
      activeAlerts,
      riskTrend: trend,
      weeklyCases,
    };
  });
}

function getRiskPriority(level: string) {
  return level === "High" ? 3 : level === "Medium" ? 2 : 1;
}

type SortField = "name" | "predicted2wk" | "growthRate" | "alertLevel" | "resourceGap" | "population";
type SortDir = "asc" | "desc";

export default function Reports() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState<"summary" | "comparison">("summary");
  const [regionFilter, setRegionFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("2025-05-01");
  const [dateTo, setDateTo] = useState("2025-05-11");
  const [sortField, setSortField] = useState<SortField>("predicted2wk");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [exportToast, setExportToast] = useState(false);

  const allReports = useMemo(() => generateDistrictReports(), []);

  const filteredReports = useMemo(() => {
    let data = [...allReports];
    if (regionFilter) data = data.filter((r) => r.region === regionFilter);
    data.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case "name":
          cmp = a.name.localeCompare(b.name);
          break;
        case "predicted2wk":
          cmp = a.predicted2wk - b.predicted2wk;
          break;
        case "growthRate":
          cmp = a.growthRate - b.growthRate;
          break;
        case "alertLevel":
          cmp = getRiskPriority(a.alertLevel) - getRiskPriority(b.alertLevel);
          break;
        case "resourceGap":
          cmp = a.resourceGap - b.resourceGap;
          break;
        case "population":
          cmp = a.population - b.population;
          break;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return data;
  }, [allReports, regionFilter, sortField, sortDir]);

  const highRiskCount = filteredReports.filter((r) => r.alertLevel === "High").length;
  const totalPredicted2wk = filteredReports.reduce((s, r) => s + r.predicted2wk, 0);
  const avgGrowth = filteredReports.length > 0
    ? Math.round(filteredReports.reduce((s, r) => s + r.growthRate, 0) / filteredReports.length * 10) / 10
    : 0;
  const totalResourceGap = filteredReports.reduce((s, r) => s + r.resourceGap, 0);
  const activeInterventions = alerts.filter((a) => a.status === "Active").length;
  const totalPopulation = filteredReports.reduce((s, r) => s + r.population, 0);
  const coveragePercent = totalPopulation > 0
    ? Math.round((totalPopulation / districts.reduce((s, d) => s + d.population, 0)) * 100)
    : 0;

  const weeklyTrend = useMemo(() => {
    const weeks: Record<string, number> = {};
    for (const c of historicalCases) {
      weeks[c.date] = (weeks[c.date] || 0) + c.cases;
    }
    return Object.entries(weeks)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-8)
      .map(([date, cases]) => ({ week: date.slice(5), cases }));
  }, []);

  const regionBreakdown = useMemo(() => {
    const map: Record<string, { high: number; medium: number; low: number }> = {};
    for (const r of filteredReports) {
      if (!map[r.region]) map[r.region] = { high: 0, medium: 0, low: 0 };
      map[r.region][r.alertLevel.toLowerCase() as "high" | "medium" | "low"] += 1;
    }
    return Object.entries(map).map(([region, counts]) => ({ region, ...counts }));
  }, [filteredReports]);

  const tooltipStyle = theme === "dark"
    ? { backgroundColor: "#0f172a", border: "1px solid #334155", borderRadius: "8px", fontSize: "11px", color: "#e2e8f0" }
    : { backgroundColor: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: "11px", color: "#1e293b" };
  const gridStroke = theme === "dark" ? "#1e293b" : "#e2e8f0";
  const tickFill = theme === "dark" ? "#64748b" : "#94a3b8";

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const exportCSV = () => {
    const headers = [
      t("reports_district"), t("reports_region"), t("reports_population"), t("reports_predicted_2wk"), t("reports_predicted_1mo"), t("reports_predicted_3mo"),
      t("reports_growth_rate"), t("reports_alert_level"), t("reports_resources_gap"), "Active Alerts", t("reports_risk_trend"),
    ];
    const rows = filteredReports.map((r) => [
      r.name, r.region, r.population, r.predicted2wk, r.predicted1mo, r.predicted3mo,
      `${r.growthRate}%`, r.alertLevel, r.resourceGap, r.activeAlerts, r.riskTrend,
    ]);
    const csv = [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `malariaguard-reports-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setExportToast(true);
    setTimeout(() => setExportToast(false), 2500);
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <i className="ri-arrow-up-down-line text-[10px] text-slate-400 dark:text-slate-600 ml-1" />;
    return <i className={`text-[10px] ml-1 ${sortDir === "asc" ? "ri-arrow-up-line text-teal" : "ri-arrow-down-line text-teal"}`} />;
  };

  const alertBadge: Record<string, string> = {
    High: "bg-risk-high/15 text-risk-high border-risk-high/20",
    Medium: "bg-amber/15 text-amber border-amber/20",
    Low: "bg-risk-low/15 text-risk-low border-risk-low/20",
  };

  const trendColor: Record<string, string> = {
    Rising: "text-risk-high",
    Stable: "text-amber",
    Declining: "text-risk-low",
  };

  const trendIcon: Record<string, string> = {
    Rising: "ri-arrow-up-line",
    Stable: "ri-subtract-line",
    Declining: "ri-arrow-down-line",
  };

  return (
    <div className="animate-fade-in space-y-5">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-xl font-display font-bold text-slate-900 dark:text-white">{t("reports_title")}</h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{t("reports_subtitle")}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs text-slate-700 dark:text-slate-300">
            <i className="ri-calendar-line text-slate-400 dark:text-slate-500" />
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="bg-transparent outline-none text-xs text-slate-700 dark:text-slate-300 w-[110px]"
            />
            <span className="text-slate-400 dark:text-slate-500">to</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="bg-transparent outline-none text-xs text-slate-700 dark:text-slate-300 w-[110px]"
            />
          </div>
          <select
            value={regionFilter}
            onChange={(e) => setRegionFilter(e.target.value)}
            className="px-3 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-xs text-slate-700 dark:text-slate-300 focus:outline-none focus:border-teal/50"
          >
            <option value="">{t("reports_all_regions")}</option>
            {regions.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          <button
            onClick={exportCSV}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors whitespace-nowrap"
          >
            <i className="ri-download-line" />
            {t("reports_export_csv")}
          </button>
        </div>
      </div>

      {exportToast && (
        <div className="fixed bottom-5 right-5 z-50 animate-slide-up">
          <div className="bg-teal-muted/80 backdrop-blur-sm border border-teal/30 rounded-lg px-4 py-3 flex items-center gap-2 shadow-glow">
            <i className="ri-check-line text-teal" />
            <span className="text-sm text-teal">{t("reports_exported")}</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
        {[
          { label: t("reports_total_districts"), value: filteredReports.length, icon: "ri-map-pin-line", color: "text-slate-700 dark:text-slate-300" },
          { label: t("reports_high_risk"), value: highRiskCount, icon: "ri-error-warning-line", color: "text-risk-high" },
          { label: t("reports_cases_this_week"), value: totalPredicted2wk.toLocaleString(), icon: "ri-virus-line", color: "text-teal" },
          { label: t("reports_avg_growth"), value: `${avgGrowth > 0 ? "+" : ""}${avgGrowth}%`, icon: "ri-arrow-up-line", color: avgGrowth > 10 ? "text-risk-high" : "text-amber" },
          { label: t("reports_resources_gap"), value: totalResourceGap.toLocaleString(), icon: "ri-archive-line", color: "text-amber" },
          { label: t("reports_coverage"), value: `${coveragePercent}%`, icon: "ri-shield-check-line", color: "text-risk-low" },
        ].map((kpi) => (
          <div key={kpi.label} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-4">
            <div className="flex items-center gap-2 mb-2">
              <i className={`${kpi.icon} text-sm text-slate-400 dark:text-slate-500`} />
              <span className="text-[10px] text-slate-500 dark:text-slate-500 uppercase tracking-wider">{kpi.label}</span>
            </div>
            <p className={`text-lg font-bold font-display ${kpi.color}`}>{kpi.value}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-1 p-1 rounded-xl bg-slate-100 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-800 w-fit">
        <button
          onClick={() => setActiveTab("summary")}
          className={`px-4 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
            activeTab === "summary"
              ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm"
              : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
          }`}
        >
          <i className="ri-file-list-3-line mr-1.5" />
          {t("reports_outbreak_summary")}
        </button>
        <button
          onClick={() => setActiveTab("comparison")}
          className={`px-4 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
            activeTab === "comparison"
              ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm"
              : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
          }`}
        >
          <i className="ri-bar-chart-grouped-line mr-1.5" />
          {t("reports_district_comparison")}
        </button>
      </div>

      {activeTab === "summary" && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
          <div className="xl:col-span-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white font-display">{t("reports_weekly_trend")}</h3>
                <p className="text-[10px] text-slate-500 dark:text-slate-500 mt-0.5">{t("reports_national_confirmed_8wk")}</p>
              </div>
              <span className="text-[10px] px-2 py-1 rounded-full bg-teal-muted/30 text-teal border border-teal/15 font-mono">{t("common_live")}</span>
            </div>
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={weeklyTrend} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                  <XAxis dataKey="week" tick={{ fontSize: 10, fill: tickFill }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: tickFill }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [value.toLocaleString(), t("alerts_cases")]} />
                  <Line type="monotone" dataKey="cases" stroke="#2dd4bf" strokeWidth={2.5} dot={{ r: 3, fill: "#2dd4bf" }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="space-y-5">
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-5">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white font-display mb-4">{t("reports_outbreak_metrics")}</h3>
              <div className="space-y-3">
                {[
                  { label: t("reports_active_interventions_metric"), value: activeInterventions, icon: "ri-shield-check-line", color: "text-teal" },
                  { label: t("reports_pop_covered_metric"), value: `${(totalPopulation / 1000000).toFixed(1)}M`, icon: "ri-team-line", color: "text-slate-700 dark:text-slate-300" },
                  { label: t("reports_outbreaks_prevented"), value: "3", icon: "ri-heart-pulse-line", color: "text-risk-low" },
                  { label: t("reports_rdt_prepositioned"), value: "2,400", icon: "ri-test-tube-line", color: "text-amber" },
                  { label: t("reports_llin_distributed"), value: "8,500", icon: "ri-shield-line", color: "text-teal" },
                ].map((m) => (
                  <div key={m.label} className="flex items-center justify-between py-2 border-b border-slate-200/50 dark:border-slate-800/50 last:border-0">
                    <div className="flex items-center gap-2">
                      <i className={`${m.icon} text-xs text-slate-400 dark:text-slate-500`} />
                      <span className="text-xs text-slate-500 dark:text-slate-400">{m.label}</span>
                    </div>
                    <span className={`text-sm font-semibold font-mono ${m.color}`}>{m.value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-5">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white font-display mb-4">{t("reports_risk_by_region")}</h3>
              <div className="h-[180px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={regionBreakdown} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                    <XAxis dataKey="region" tick={{ fontSize: 9, fill: tickFill }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 9, fill: tickFill }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="high" stackId="a" fill="#ef4444" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="medium" stackId="a" fill="#f59e0b" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="low" stackId="a" fill="#22c55e" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center justify-center gap-4 mt-3">
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-sm bg-risk-high" />
                  <span className="text-[10px] text-slate-500 dark:text-slate-500">{t("common_high")}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-sm bg-risk-medium" />
                  <span className="text-[10px] text-slate-500 dark:text-slate-500">{t("common_medium")}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-sm bg-risk-low" />
                  <span className="text-[10px] text-slate-500 dark:text-slate-500">{t("common_low")}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "comparison" && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white font-display">{t("reports_district_comparison")}</h3>
              <p className="text-[10px] text-slate-500 dark:text-slate-500 mt-0.5">
                {t("reports_comparing", { count: filteredReports.length })}
              </p>
            </div>
            <div className="hidden md:flex items-center gap-2 text-[10px] text-slate-400 dark:text-slate-500">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-risk-high" /> {t("common_high")}</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-risk-medium" /> {t("common_medium")}</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-risk-low" /> {t("common_low")}</span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800">
                  {[
                    { key: "name" as SortField, label: t("reports_district") },
                    { key: "population" as SortField, label: t("reports_population") },
                    { key: "predicted2wk" as SortField, label: t("reports_predicted_2wk") },
                    { key: "growthRate" as SortField, label: t("reports_growth_rate") },
                    { key: "alertLevel" as SortField, label: t("reports_alert_level") },
                  ].map((col) => (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      className="px-4 py-3 text-[10px] font-semibold text-slate-500 dark:text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 dark:hover:text-slate-300 transition-colors select-none"
                    >
                      <span className="flex items-center gap-1">{col.label}<SortIcon field={col.key} /></span>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-[10px] font-semibold text-slate-500 dark:text-slate-500 uppercase tracking-wider">{t("reports_predicted_1mo")}</th>
                  <th className="px-4 py-3 text-[10px] font-semibold text-slate-500 dark:text-slate-500 uppercase tracking-wider">{t("reports_predicted_3mo")}</th>
                  <th className="px-4 py-3 text-[10px] font-semibold text-slate-500 dark:text-slate-500 uppercase tracking-wider">{t("reports_resources_gap")}</th>
                  <th className="px-4 py-3 text-[10px] font-semibold text-slate-500 dark:text-slate-500 uppercase tracking-wider">{t("reports_risk_trend")}</th>
                  <th className="px-4 py-3 text-[10px] font-semibold text-slate-500 dark:text-slate-500 uppercase tracking-wider">{t("reports_actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredReports.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-slate-200/50 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-xs font-medium text-slate-800 dark:text-slate-200">{r.name}</p>
                        <p className="text-[10px] text-slate-400 dark:text-slate-500">{r.region}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-slate-700 dark:text-slate-300">{r.population.toLocaleString()}</td>
                    <td className="px-4 py-3 text-xs font-mono text-slate-700 dark:text-slate-300">{r.predicted2wk.toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-mono font-medium ${r.growthRate > 30 ? "text-risk-high" : r.growthRate > 10 ? "text-amber" : "text-risk-low"}`}>
                        {r.growthRate > 0 ? "+" : ""}{r.growthRate}%
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-medium border ${alertBadge[r.alertLevel]}`}>
                        {r.alertLevel === "High" ? t("common_high") : r.alertLevel === "Medium" ? t("common_medium") : t("common_low")}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-slate-500 dark:text-slate-400">{r.predicted1mo.toLocaleString()}</td>
                    <td className="px-4 py-3 text-xs font-mono text-slate-500 dark:text-slate-400">{r.predicted3mo.toLocaleString()}</td>
                    <td className="px-4 py-3">
                      {r.resourceGap > 0 ? (
                        <span className="text-xs font-mono text-amber font-medium">+{r.resourceGap}</span>
                      ) : (
                        <span className="text-xs text-emerald">{t("reports_ok")}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`flex items-center gap-1 text-xs font-medium ${trendColor[r.riskTrend]}`}>
                        <i className={`${trendIcon[r.riskTrend]} text-[10px]`} />
                        {r.riskTrend === "Rising" ? t("common_critical") : r.riskTrend === "Stable" ? t("common_warning") : t("common_normal")}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Link
                          to={`/predictions?district=${r.id}`}
                          className="w-7 h-7 flex items-center justify-center rounded-md text-slate-400 dark:text-slate-500 hover:text-teal hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                          title={t("reports_view_predictions")}
                        >
                          <i className="ri-line-chart-line text-xs" />
                        </Link>
                        <Link
                          to={`/alerts?district=${r.id}`}
                          className="w-7 h-7 flex items-center justify-center rounded-md text-slate-400 dark:text-slate-500 hover:text-amber hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                          title={t("reports_view_alerts")}
                        >
                          <i className="ri-alarm-warning-line text-xs" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredReports.length === 0 && (
                  <tr>
                    <td colSpan={11} className="px-4 py-12 text-center text-sm text-slate-500 dark:text-slate-500">
                      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 mx-auto mb-3">
                        <i className="ri-inbox-line text-xl text-slate-400 dark:text-slate-500" />
                      </div>
                      {t("reports_no_data")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between text-[10px] text-slate-500 dark:text-slate-500">
            <span>{t("reports_showing_districts", { count: filteredReports.length, total: allReports.length })}</span>
            {regionFilter && (
              <button
                onClick={() => setRegionFilter("")}
                className="text-teal hover:text-teal-hover transition-colors"
              >
                {t("reports_clear_region")}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}